import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';

const LandlordLayout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('John');
  const [userAvatar, setUserAvatar] = useState('');
  const [userRole, setUserRole] = useState('Landlord');
  
  const landlordMenuItems = [
    { path: '/landlord/dashboard', label: 'Dashboard', icon: '/assets/images/sidebardashboardIcon.svg' },
    { path: '/landlord/properties', label: 'My Properties', icon: '/assets/images/sidebar_properties.png' },
    { path: '/landlord/leases', label: 'Leases', icon: '/assets/images/file-04.png' },
    { path: '/landlord/notifications', label: 'Notifications', icon: '/assets/images/sidebarNotificationIcon.svg' },
    { path: '/landlord/settings', label: 'Profile', icon: '/assets/images/sidebarSettingsIcon.svg' },
  ];
  
  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      // First try to get from userData
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const userDetails = userData?.user_details || {};
        const roleValue = userDetails?.role ?? userData?.role ?? localStorage.getItem('role');
        const normalizedRole =
          roleValue === 1 || roleValue === '1' || roleValue === 'landlord'
            ? 'Landlord'
            : roleValue === 2 || roleValue === '2' || roleValue === 'tenant'
              ? 'Tenant'
              : '';
        const firstName = userDetails?.first_name || '';
        const lastName = userDetails?.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
          return {
            userName: fullName,
            // Only use actual profile image; otherwise let Header show initials
            userAvatar: userDetails?.profile_image || '',
            userRole: normalizedRole || 'Landlord',
          };
        }
      }
      // Fallback to userName if userData doesn't have name
      const storedUserName = localStorage.getItem('userName');
      const storedRole = localStorage.getItem('role');
      const normalizedStoredRole =
        storedRole === '1' || storedRole === 1 || storedRole === 'landlord'
          ? 'Landlord'
          : storedRole === '2' || storedRole === 2 || storedRole === 'tenant'
            ? 'Tenant'
            : '';
      if (storedUserName) {
        return {
          userName: storedUserName,
          userAvatar: localStorage.getItem('userAvatar') || '',
          userRole: normalizedStoredRole || 'Landlord',
        };
      }
    } catch (error) {
      console.error('Error reading user info from localStorage:', error);
    }
    return { userName: 'John', userAvatar: '', userRole: 'Landlord' };
  };
  
  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Update user info when component mounts or storage changes
  useEffect(() => {
    const updateUserInfo = () => {
      const { userName: name, userAvatar: avatar, userRole: roleLabel } = getUserInfo();
      setUserName(name);
      setUserAvatar(avatar);
      setUserRole(roleLabel || 'Landlord');
    };
    
    // Initial load
    updateUserInfo();
    
    // Listen for custom event when userData is updated (from same window)
    const handleUserDataUpdate = () => {
      updateUserInfo();
    };
    
    // Also listen for storage events (when localStorage changes from other tabs/windows)
    const handleStorageChange = () => {
      updateUserInfo();
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get title and subtitle based on current route
  const getTitleAndSubtitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return { title: 'Dashboard', subtitle: null };
    if (path.includes('/properties/add')) return { title: 'Add New Property', subtitle: 'List a new rental property for tenant screening' };
    if (path.includes('/properties')) return { title: 'My Properties', subtitle: 'Manage your rental listings' };
    if (path.includes('/leases')) return { title: 'Leases', subtitle: 'Lease documents and rent details for your tenants' };
    if (path.includes('/notifications')) return { title: 'Notifications', subtitle: 'Stay updated on your account activity' };
    if (path.includes('/settings')) return { title: 'Settings', subtitle: 'Manage your account and preferences' };
    return { title: 'Dashboard', subtitle: null };
  };

  // Get breadcrumb based on current route
  const getBreadcrumb = () => {
    const path = location.pathname;
    // Check for tenant detail page first (most specific)
    if (path.match(/\/landlord\/properties\/\d+\/tenant\/\d+/)) {
      const propertyId = path.match(/\/properties\/(\d+)/)?.[1];
      return [
        { label: 'Properties', path: '/landlord/properties' },
        { label: 'Property Detail', path: `/landlord/properties/${propertyId}` },
        { label: 'Tenant Detail', path: null }
      ];
    }
    // Check for property detail page
    if (path.match(/\/landlord\/properties\/\d+$/)) {
      return [
        { label: 'Properties', path: '/landlord/properties' },
        { label: 'Property Detail', path: null }
      ];
    }
    // Lease detail page (the route param is the lease id)
    if (path.match(/^\/landlord\/leases\/[^/]+$/)) {
      return [
        { label: 'Leases', path: '/landlord/leases' },
        { label: 'Lease Detail', path: null }
      ];
    }
    return null;
  };


  const { title, subtitle } = getTitleAndSubtitle();
  const breadcrumb = getBreadcrumb();

  return (
    <div className="landlord-layout">
      <Sidebar 
        menuItems={landlordMenuItems} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />
      <div className="landlord-layout-content">
        <Header 
          title={title} 
          subtitle={subtitle} 
          userName={userName} 
          userAvatar={userAvatar}
          userRole={userRole}
          breadcrumb={breadcrumb}
          onSidebarToggle={() => setSidebarOpen(true)}
        />
        <main className="landlord-layout-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default LandlordLayout;
